import { Link } from 'react-router-dom';
import { NEWS_ITEMS, STATS, WHY_ITEMS } from '../utils/homeData.js';
import { withBasePath } from '../utils/paths.js';

export function NewsSection() {
  return (
    <section className="py-16 sm:py-20" id="news">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="v4-reveal mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-nkc-gold">Airport news</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-nkc-navy sm:text-4xl">Latest updates</h2>
          </div>
          <Link to="/news" className="text-sm font-bold text-nkc-blue hover:underline">
            All news →
          </Link>
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {NEWS_ITEMS.map((n) => (
            <article
              key={n.title}
              className="v4-reveal overflow-hidden rounded-3xl bg-white shadow-soft ring-1 ring-nkc-blue/10"
            >
              <img src={withBasePath(n.image)} alt="" className="aspect-[16/10] w-full object-cover" loading="lazy" />
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-nkc-muted">
                  <span className="rounded-full bg-nkc-mist px-2.5 py-1 text-nkc-blue">{n.category}</span>
                  <time>{n.date}</time>
                </div>
                <h3 className="mt-3 font-display text-xl font-bold text-nkc-navy">{n.title}</h3>
                <Link to={n.to} className="mt-4 inline-flex text-sm font-bold text-nkc-blue hover:underline">
                  Read more →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StatsSection() {
  return (
    <section className="bg-nkc-navy py-14 text-white sm:py-16" id="stats">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-4 sm:gap-x-10 sm:px-6 lg:grid-cols-4 lg:gap-x-12 lg:px-8">
        {STATS.map((s) => (
          <div key={s.label} className="v4-reveal min-w-0 text-center">
            <div className="v4-stat-num text-nkc-gold" data-count={s.count}>
              0
            </div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-white/70 sm:text-sm">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function WhyChoose() {
  return (
    <section className="py-16 sm:py-20" id="why">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="v4-reveal mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-nkc-gold">Why choose NKC</p>
          <h2 className="mt-2 font-display text-3xl font-bold text-nkc-navy sm:text-4xl">Built for modern travel</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_ITEMS.map((w) => (
            <Link
              key={w.title}
              to={w.to}
              className="v4-reveal rounded-3xl border border-nkc-blue/10 bg-nkc-mist p-6 shadow-soft transition hover:-translate-y-1 hover:border-nkc-blue/25 hover:shadow-lift"
            >
              <div className="mb-4 h-1.5 w-12 rounded-full bg-nkc-gold" />
              <h3 className="font-display text-lg font-bold text-nkc-navy">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-nkc-muted">{w.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AdvertiseSection() {
  return (
    <section className="py-16 sm:py-20" id="advertise">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="v4-reveal overflow-hidden rounded-[2rem] bg-gradient-to-br from-nkc-blue via-nkc-navy to-[#071833] px-6 py-10 text-white shadow-lift sm:px-10 lg:flex lg:items-center lg:justify-between lg:gap-12 lg:px-14 lg:py-14">
          <div className="max-w-xl">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">
              Maintenance as a Service
            </h2>
            <p className="mt-3 text-white/75">
              Partner with NKC for end-to-end upkeep of terminal systems, airside assets, and facilities. Reliable
              performance for Mauritania’s busiest gateway.
            </p>
            <ul className="mt-5 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide text-white/80">
              {['Terminal systems', 'Airside assets', 'Facilities', 'Preventive care'].map((t) => (
                <li key={t} className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5">
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/business-opportunities" className="rounded-2xl bg-nkc-gold px-5 py-3 text-sm font-bold text-nkc-navy">
                Explore MaaS options
              </Link>
              <Link to="/contact" className="rounded-2xl border border-white/30 px-5 py-3 text-sm font-bold text-white">
                Talk to commercial
              </Link>
            </div>
          </div>
          <div className="relative mt-10 w-full max-w-sm shrink-0 lg:mt-0">
            <div className="overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-lift backdrop-blur-sm">
              <img
                src={withBasePath('/images/airport/airport-shopping-area.jpg')}
                alt="Airport facilities and maintenance operations"
                className="h-44 w-full object-cover sm:h-52"
                loading="lazy"
                decoding="async"
                width="640"
                height="360"
              />
              <div className="space-y-3 p-5">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-nkc-gold">Maintenance as a Service</p>
                <p className="text-sm leading-relaxed text-white/85">
                  Keep critical airport systems running with planned maintenance and rapid response support.
                </p>
                <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3 text-xs text-white/65">
                  <span>24/7 readiness</span>
                  <span className="font-bold text-white">Partner with NKC</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CtaSection() {
  return (
    <section className="py-16 sm:py-20" id="cta">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="v4-reveal rounded-[2rem] bg-nkc-blue px-6 py-12 text-center text-white shadow-lift sm:px-12">
          <h2 className="font-display text-3xl font-bold sm:text-5xl">Ready for your journey?</h2>
          <p className="mx-auto mt-4 max-w-xl text-white/80">
            Check live status, explore services, or start planning your arrival at NKC.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/flights" className="rounded-2xl bg-nkc-gold px-6 py-3 text-sm font-bold text-nkc-navy">
              Flight status
            </Link>
            <Link to="/contact" className="rounded-2xl border border-white/35 px-6 py-3 text-sm font-bold text-white">
              Contact us
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
